import React from 'react';

// SVG Icons
const TrendingUpIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendingDownIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const ShowChartIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const PercentIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const SwapVertIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
  </svg>
);

const AccessTimeIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend }) => {
  const isPositive = trend === 'positive' || (typeof value === 'number' && value > 0);
  const isNegative = trend === 'negative' || (typeof value === 'number' && value < 0);

  const getColorClass = () => {
    if (color === 'auto') {
      return isPositive ? 'text-success' : isNegative ? 'text-error' : 'text-primary';
    }
    return color === 'success' ? 'text-success' : color === 'error' ? 'text-error' : 'text-primary';
  };

  const getBorderColor = () => {
    if (color === 'auto') {
      return isPositive ? 'border-l-success' : isNegative ? 'border-l-error' : 'border-l-primary';
    }
    return color === 'success' ? 'border-l-success' : color === 'error' ? 'border-l-error' : 'border-l-primary';
  };

  const getBgColor = () => {
    if (color === 'auto') {
      return isPositive ? 'bg-success/10' : isNegative ? 'bg-error/10' : 'bg-primary/10';
    }
    return color === 'success' ? 'bg-success/10' : color === 'error' ? 'bg-error/10' : 'bg-primary/10';
  };

  return (
    <div className={`card bg-base-100 h-full border-l-4 ${getBorderColor()} shadow hover:shadow-lg hover:-translate-y-0.5 transition-all`}>
      <div className="card-body p-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs uppercase tracking-wide text-base-content/60">{title}</p>
            <p className={`text-xl font-bold mt-1 ${getColorClass()}`}>{value}</p>
            {subtitle && (
              <p className="text-xs text-base-content/60">{subtitle}</p>
            )}
          </div>
          {Icon && (
            <div className={`p-2 rounded-full ${getBgColor()}`}>
              <Icon className={`h-6 w-6 ${getColorClass()}`} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BacktestSummary = ({ summary, strategyName, symbol, timeframe, tradingType, startDate, endDate, chargesSummary }) => {
  if (!summary) return null;

  // Helper to safely get numeric values with defaults
  const safeNumber = (value, defaultValue = 0) => {
    return typeof value === 'number' && !isNaN(value) ? value : defaultValue;
  };

  const formatPercent = (value) => {
    const numValue = safeNumber(value);
    const sign = numValue >= 0 ? '+' : '';
    return `${sign}${numValue.toFixed(2)}%`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(safeNumber(value));
  };

  // Extract values with fallbacks for different field names
  const totalReturn = safeNumber(summary.totalReturn);
  const netProfit = safeNumber(summary.netProfit ?? summary.profitLoss);
  const sharpeRatio = safeNumber(summary.sharpeRatio);
  const maxDrawdown = safeNumber(summary.maxDrawdown);
  const winRate = safeNumber(summary.winRate);
  const winningTrades = safeNumber(summary.winningTrades);
  const losingTrades = safeNumber(summary.losingTrades);
  const profitFactor = safeNumber(summary.profitFactor);
  const totalTrades = safeNumber(summary.totalTrades);
  const avgTradeDuration = summary.avgTradeDuration || 'N/A';

  return (
    <div className="mb-8">
      {/* Strategy Header */}
      <div className="mb-6">
        <h2 className="text-lg font-bold">{strategyName || 'Strategy Backtest'}</h2>
        <div className="text-sm text-base-content/60 flex flex-wrap gap-x-4 gap-y-1 mt-1">
          <span>{symbol || 'N/A'}</span>
          <span>•</span>
          <span>{timeframe || 'N/A'}</span>
          <span>•</span>
          <span className={`badge badge-sm ${tradingType === 'INTRADAY' ? 'badge-info' : 'badge-ghost'}`}>{tradingType || 'Unknown'}</span>
          <span>•</span>
          <span>{new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}</span>
          <span>•</span>
          <span>{summary.totalTrades || 0} trades</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
        <MetricCard
          title="Total Return"
          value={formatPercent(totalReturn)}
          subtitle={formatCurrency(netProfit)}
          icon={totalReturn >= 0 ? TrendingUpIcon : TrendingDownIcon}
          color="auto"
          trend={totalReturn >= 0 ? 'positive' : 'negative'}
        />

        <MetricCard
          title="Sharpe Ratio"
          value={sharpeRatio.toFixed(2)}
          subtitle="Risk-adjusted"
          icon={ShowChartIcon}
          color={sharpeRatio >= 1 ? 'success' : sharpeRatio >= 0.5 ? 'primary' : 'error'}
        />

        <MetricCard
          title="Max Drawdown"
          value={`-${maxDrawdown.toFixed(2)}%`}
          subtitle="Peak to trough"
          icon={TrendingDownIcon}
          color={maxDrawdown <= 10 ? 'success' : maxDrawdown <= 20 ? 'primary' : 'error'}
        />

        <MetricCard
          title="Win Rate"
          value={`${winRate.toFixed(1)}%`}
          subtitle={`${winningTrades}W / ${losingTrades}L`}
          icon={PercentIcon}
          color={winRate >= 50 ? 'success' : 'error'}
        />

        <MetricCard
          title="Profit Factor"
          value={profitFactor.toFixed(2)}
          subtitle="Gross P / Gross L"
          icon={SwapVertIcon}
          color={profitFactor >= 1.5 ? 'success' : profitFactor >= 1 ? 'primary' : 'error'}
        />

        <MetricCard
          title="Avg Duration"
          value={avgTradeDuration}
          subtitle={`${totalTrades} trades`}
          icon={AccessTimeIcon}
          color="primary"
        />
      </div>
      {/* Charges Summary */}
      {chargesSummary && chargesSummary.totalCharges > 0 && (
        <div className="mt-6">
          <div className="divider text-xs text-base-content/50 my-2">Charges Summary</div>
          <div className="card bg-base-100 shadow border border-warning/20">
            <div className="card-body p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">

                {/* Net P&L headline */}
                <div className="flex-shrink-0 text-center md:border-r md:border-base-300 md:pr-6">
                  <p className="text-xs uppercase tracking-wide text-base-content/60">Net P&L (after charges)</p>
                  <p className={`text-2xl font-bold mt-1 ${chargesSummary.netProfitLoss >= 0 ? 'text-success' : 'text-error'
                    }`}>
                    {formatCurrency(chargesSummary.netProfitLoss)}
                  </p>
                  <p className="text-xs text-warning mt-0.5">
                    Total Charges: {formatCurrency(chargesSummary.totalCharges)}
                  </p>
                </div>

                {/* Breakdown bars */}
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'Brokerage', value: chargesSummary.totalBrokerage },
                    { label: 'STT', value: chargesSummary.totalStt },
                    { label: 'GST', value: chargesSummary.totalGst },
                    { label: 'Stamp Duty', value: chargesSummary.totalStampDuty },
                    { label: 'Txn Charges', value: chargesSummary.totalTransactionCharges },
                    { label: 'SEBI Fee', value: chargesSummary.totalSebiTurnoverFee },
                  ].map(({ label, value }) => {
                    const pct = chargesSummary.totalCharges > 0
                      ? ((value / chargesSummary.totalCharges) * 100).toFixed(1)
                      : 0;
                    return (
                      <div key={label} className="text-center">
                        <p className="text-xs text-base-content/60 truncate">{label}</p>
                        <p className="font-mono font-semibold text-sm text-warning">{formatCurrency(value)}</p>
                        <div className="w-full bg-base-200 rounded-full h-1 mt-1">
                          <div
                            className="bg-warning h-1 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-xs text-base-content/40 mt-0.5">{pct}%</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BacktestSummary;
